// src/Bike Quotation Generator/QuotationGenerator.js
import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, X, Save, Download, Share2, Calculator, 
  Calendar, DollarSign, Bike, Search, Filter, Eye, Edit,
  Building, User, Shield, AlertTriangle, Check, Copy,
  ChevronDown, ChevronUp, MapPin, Clock, Phone, Mail
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';

// Bike Selection Component
const BikeSelection = ({ 
  bikes, 
  selectedBikes, 
  setSelectedBikes, 
  searchTerm, 
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  quoteData
}) => {
  const [showAvailableOnly, setShowAvailableOnly] = useState(false); // Changed default to false

  // Calculate rental days between start and end date (inclusive)
  const calculateRentalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    
    return diffDays > 0 ? diffDays : 1;
  };

  // Get unique statuses from bikes for debugging
  const bikeStatuses = [...new Set(bikes.map(bike => bike.status || 'No Status'))];
  const availableBikes = bikes.filter(bike => bike.status === 'available').length;

  // Filter bikes based on search and filters
  const filteredBikes = bikes.filter(bike => {
    const matchesSearch = 
      bike.regNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bike.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bike.color?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || bike.status === filterStatus;
    
    // Fixed available filter - treat bikes without status as available
    const bikeStatus = bike.status || 'available';
    const matchesAvailable = !showAvailableOnly || bikeStatus === 'available';

    return matchesSearch && matchesStatus && matchesAvailable;
  });

  const handleBikeToggle = (bike) => {
    const isSelected = selectedBikes.some(selected => selected.id === bike.id);
    
    if (isSelected) {
      setSelectedBikes(selectedBikes.filter(selected => selected.id !== bike.id));
    } else {
      // Use calculated rental days from quote data
      const calculatedDays = calculateRentalDays(quoteData?.startDate, quoteData?.endDate);
      
      setSelectedBikes([...selectedBikes, {
        ...bike,
        quantity: 1,
        customRate: bike.dailyRate,
        rentalDays: calculatedDays
      }]);
    }
  };

  const updateSelectedBike = (bikeId, field, value) => {
    setSelectedBikes(prev => prev.map(bike => 
      bike.id === bikeId ? { ...bike, [field]: value } : bike
    ));
  };

  const getStatusColor = (status) => {
    // Handle bikes without status (treat as available)
    const normalizedStatus = status || 'available';
    
    switch (normalizedStatus) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'rented': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'retired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (bikes.length === 0) {
    return (
      <div className="text-center py-12">
        <Bike className="mx-auto h-20 w-20 text-gray-400 mb-6" />
        <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Bikes Available</h3>
        <p className="text-gray-500 text-lg">No bikes found in your bike management system.</p>
        <p className="text-gray-600 text-sm mt-2">Add bikes in Bike Management to create quotations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Search Bikes</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by registration, model..."
                className="w-full pl-10 pr-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="rented">Rented</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center space-x-2 text-gray-300">
              <input
                type="checkbox"
                checked={showAvailableOnly}
                onChange={(e) => setShowAvailableOnly(e.target.checked)}
                className="rounded text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm">Available only ({availableBikes})</span>
            </label>
          </div>
        </div>
        
        {/* Bike Status Debug Info */}
        {bikes.length > 0 && (
          <div className="mt-4 p-3 bg-gray-800 rounded-lg">
            <div className="text-xs text-gray-400 mb-2">
              <span className="font-medium">Bike Statuses in System:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {bikeStatuses.map(status => {
                const count = bikes.filter(bike => (bike.status || 'No Status') === status).length;
                return (
                  <span key={status} className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded">
                    {status}: {count}
                  </span>
                );
              })}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Total bikes: {bikes.length} • Showing: {filteredBikes.length}
              {bikes.some(bike => !bike.status) && " • Bikes without status are treated as 'available'"}
            </div>
          </div>
        )}
      </div>

      {/* Selected Bikes Summary */}
      {selectedBikes.length > 0 && (
        <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <Check className="text-green-400" size={20} />
            <h4 className="text-green-300 font-medium">Selected Bikes ({selectedBikes.length})</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {selectedBikes.map(bike => (
              <div key={bike.id} className="bg-gray-800 p-3 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-white font-medium">{bike.regNumber}</span>
                  <button
                    onClick={() => handleBikeToggle(bike)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="text-gray-300 text-sm mb-2">{bike.model}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <label className="text-gray-400">Days:</label>
                    <input
                      type="number"
                      min="1"
                      value={bike.rentalDays}
                      onChange={(e) => updateSelectedBike(bike.id, 'rentalDays', parseInt(e.target.value))}
                      className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400">Rate:</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bike.customRate}
                      onChange={(e) => updateSelectedBike(bike.id, 'customRate', parseFloat(e.target.value))}
                      className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white"
                    />
                  </div>
                </div>
                <div className="mt-2 text-orange-400 font-medium text-sm">
                  Total: ${(bike.customRate * bike.rentalDays).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Bikes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBikes.map(bike => {
          const isSelected = selectedBikes.some(selected => selected.id === bike.id);
          
          return (
            <div 
              key={bike.id} 
              className={`bg-gray-700 rounded-lg border cursor-pointer transition-all hover:border-orange-500 ${
                isSelected ? 'border-green-500 bg-green-900/20' : 'border-gray-600'
              }`}
              onClick={() => handleBikeToggle(bike)}
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-white">{bike.regNumber}</h3>
                  <div className="flex items-center gap-2">
                    {isSelected && <Check className="text-green-400" size={16} />}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bike.status)}`}>
                      {bike.status || 'available'}
                    </span>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-2">{bike.model} {bike.year && `(${bike.year})`}</p>
                
                {bike.color && (
                  <p className="text-gray-400 text-sm mb-2">Color: {bike.color}</p>
                )}
                
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-600">
                  <span className="text-gray-400">Daily Rate:</span>
                  <span className="text-orange-400 font-semibold">${bike.dailyRate}</span>
                </div>
                
                {bike.description && (
                  <p className="text-gray-500 text-xs mt-2 truncate">{bike.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredBikes.length === 0 && (
        <div className="text-center py-8">
          <Filter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No bikes found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

// Quote Form Component
const QuoteForm = ({ 
  quoteData, 
  setQuoteData, 
  selectedBikes, 
  setSelectedBikes,
  currentCompany,
  currentUser 
}) => {
  // Calculate rental days between start and end date (inclusive)
  const calculateRentalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    
    return diffDays > 0 ? diffDays : 1;
  };

  const handleInputChange = (field, value) => {
    setQuoteData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate rental days when dates change
    if (field === 'startDate' || field === 'endDate') {
      const newQuoteData = { ...quoteData, [field]: value };
      const calculatedDays = calculateRentalDays(newQuoteData.startDate, newQuoteData.endDate);
      
      // Update all selected bikes with new rental days
      setSelectedBikes(prev => prev.map(bike => ({
        ...bike,
        rentalDays: calculatedDays
      })));
    }
  };

  const subtotal = selectedBikes.reduce((sum, bike) => sum + (bike.customRate * bike.rentalDays), 0);
  const taxAmount = subtotal * (quoteData.taxRate / 100);
  const discountAmount = subtotal * (quoteData.discountRate / 100);
  const total = subtotal + taxAmount - discountAmount;

  // Get calculated rental days
  const calculatedDays = calculateRentalDays(quoteData.startDate, quoteData.endDate);

  return (
    <div className="space-y-6">
      {/* Customer Information */}
      <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Customer Name *</label>
            <input
              type="text"
              value={quoteData.customerName}
              onChange={(e) => handleInputChange('customerName', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Enter customer name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              value={quoteData.customerEmail}
              onChange={(e) => handleInputChange('customerEmail', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Enter email address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
            <input
              type="tel"
              value={quoteData.customerPhone}
              onChange={(e) => handleInputChange('customerPhone', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
            <input
              type="text"
              value={quoteData.customerAddress}
              onChange={(e) => handleInputChange('customerAddress', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Enter address"
            />
          </div>
        </div>
      </div>

      {/* Rental Details */}
      <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Rental Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Start Date *</label>
            <input
              type="date"
              value={quoteData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">End Date *</label>
            <input
              type="date"
              value={quoteData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              min={quoteData.startDate}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Pickup Location</label>
            <input
              type="text"
              value={quoteData.pickupLocation}
              onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
              placeholder="Enter pickup location"
            />
          </div>
        </div>
        
        {/* Rental Duration Display */}
        {quoteData.startDate && quoteData.endDate && (
          <div className="mt-4 p-4 bg-blue-900/20 border border-blue-600/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="text-blue-400" size={20} />
              <div>
                <h4 className="text-blue-300 font-medium">Rental Duration Calculated</h4>
                <p className="text-blue-200 text-sm">
                  <strong>{calculatedDays} day(s)</strong> from {new Date(quoteData.startDate).toLocaleDateString()} to {new Date(quoteData.endDate).toLocaleDateString()} (inclusive)
                </p>
                <p className="text-blue-100 text-xs mt-1">
                  All selected bikes have been updated with this duration • Both start and end dates are included
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pricing */}
      <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Pricing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Tax Rate (%)</label>
            <input
              type="number"
              step="0.01"
              value={quoteData.taxRate}
              onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Discount Rate (%)</label>
            <input
              type="number"
              step="0.01"
              value={quoteData.discountRate}
              onChange={(e) => handleInputChange('discountRate', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-gray-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-gray-300">
            <span>Subtotal:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {quoteData.taxRate > 0 && (
            <div className="flex justify-between text-gray-300">
              <span>Tax ({quoteData.taxRate}%):</span>
              <span>${taxAmount.toFixed(2)}</span>
            </div>
          )}
          {quoteData.discountRate > 0 && (
            <div className="flex justify-between text-green-400">
              <span>Discount ({quoteData.discountRate}%):</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-gray-600 pt-2 flex justify-between text-orange-400 font-bold text-lg">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Additional Notes</h3>
        <textarea
          value={quoteData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
          placeholder="Enter any additional notes or terms..."
        />
      </div>
    </div>
  );
};

// Quote Preview Component
const QuotePreview = ({ 
  quoteData, 
  selectedBikes, 
  currentCompany, 
  currentUser, 
  onEdit,
  onSave 
}) => {
  // Calculate rental days between start and end date (inclusive)
  const calculateRentalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    
    return diffDays > 0 ? diffDays : 1;
  };

  const subtotal = selectedBikes.reduce((sum, bike) => sum + (bike.customRate * bike.rentalDays), 0);
  const taxAmount = subtotal * (quoteData.taxRate / 100);
  const discountAmount = subtotal * (quoteData.discountRate / 100);
  const total = subtotal + taxAmount - discountAmount;
  
  const calculatedDays = calculateRentalDays(quoteData.startDate, quoteData.endDate);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    const quoteText = `Bike Rental Quote #${quoteData.quoteNumber}\n\nCustomer: ${quoteData.customerName}\nPeriod: ${quoteData.startDate} to ${quoteData.endDate}\nBikes: ${selectedBikes.length}\nTotal: $${total.toFixed(2)}\n\nGenerated by ${currentCompany?.name || 'Bike Manager'}`;
    
    navigator.clipboard.writeText(quoteText).then(() => {
      alert('Quote details copied to clipboard!');
    });
  };

  return (
    <div className="max-w-4xl mx-auto bg-white text-black p-8 rounded-lg shadow-lg">
      {/* Header */}
      <div className="border-b-2 border-gray-200 pb-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">QUOTATION</h1>
            <p className="text-gray-600 mt-1">Quote #{quoteData.quoteNumber}</p>
            <p className="text-gray-600">Date: {new Date(quoteData.quoteDate).toLocaleDateString()}</p>
          </div>
          <div className="text-right">
            <h2 className="text-xl font-bold text-gray-800">{currentCompany?.name || 'Bike Rental Company'}</h2>
            <p className="text-gray-600">{currentCompany?.address || 'Company Address'}</p>
            <p className="text-gray-600">{currentCompany?.phoneNumber || 'Phone Number'}</p>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill To:</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="font-medium">{quoteData.customerName}</p>
          {quoteData.customerEmail && <p className="text-gray-600">{quoteData.customerEmail}</p>}
          {quoteData.customerPhone && <p className="text-gray-600">{quoteData.customerPhone}</p>}
          {quoteData.customerAddress && <p className="text-gray-600">{quoteData.customerAddress}</p>}
        </div>
      </div>

      {/* Rental Details */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Rental Details:</h3>
        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <p><span className="font-medium">Start Date:</span> {quoteData.startDate}</p>
            <p><span className="font-medium">End Date:</span> {quoteData.endDate}</p>
          </div>
          <div>
            <p><span className="font-medium">Pickup Location:</span> {quoteData.pickupLocation}</p>
            <p><span className="font-medium">Duration:</span> {calculatedDays} day(s)</p>
          </div>
        </div>
      </div>

      {/* Bikes Table */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Bikes:</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Registration</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Model</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Days</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Daily Rate</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {selectedBikes.map(bike => (
                <tr key={bike.id}>
                  <td className="border border-gray-300 px-4 py-2">{bike.regNumber}</td>
                  <td className="border border-gray-300 px-4 py-2">{bike.model}</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{bike.rentalDays}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">${bike.customRate.toFixed(2)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">${(bike.customRate * bike.rentalDays).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="mb-6">
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {quoteData.taxRate > 0 && (
              <div className="flex justify-between">
                <span>Tax ({quoteData.taxRate}%):</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            {quoteData.discountRate > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({quoteData.discountRate}%):</span>
                <span>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t-2 border-gray-300 pt-2 flex justify-between font-bold text-lg">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {quoteData.notes && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Notes:</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{quoteData.notes}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t-2 border-gray-200 pt-4 text-center text-gray-600">
        <p>Thank you for choosing {currentCompany?.name || 'our bike rental service'}!</p>
        <p className="text-sm mt-2">Generated by {currentUser?.username} on {new Date().toLocaleDateString()}</p>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-center space-x-4 print:hidden">
        <button
          onClick={onEdit}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
        >
          <Edit size={16} />
          <span>Edit Quote</span>
        </button>
        <button
          onClick={onSave}
          className="bg-green-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
        >
          <Save size={16} />
          <span>Save Quote</span>
        </button>
        <button
          onClick={handlePrint}
          className="bg-gray-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-700 transition-colors"
        >
          <Download size={16} />
          <span>Print</span>
        </button>
        <button
          onClick={handleShare}
          className="bg-orange-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Share2 size={16} />
          <span>Share</span>
        </button>
      </div>
    </div>
  );
};

// Main QuotationGenerator Component
const QuotationGenerator = ({
  currentUser,
  currentCompany, 
  getCompanyContext,
  hasPermission
}) => {
  const [bikes, setBikes] = useState([]);
  const [selectedBikes, setSelectedBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Select Bikes, 2: Quote Form, 3: Preview
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [quoteData, setQuoteData] = useState({
    quoteNumber: 'Q' + Date.now(),
    quoteDate: new Date().toISOString().split('T')[0],
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    startDate: new Date().toISOString().split('T')[0], // Today
    endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow (2 days total: today + tomorrow)
    pickupLocation: '',
    taxRate: 0,
    discountRate: 0,
    notes: ''
  });

  // Get current company context
  const companyId = getCompanyContext();

  // Multi-tenant Firestore collections
  const managedBikesCollection = collection(db, 'managedBikes');
  const quotesCollection = collection(db, 'quotes');

  // Permission check
  useEffect(() => {
    if (!hasPermission('quotationGenerator')) {
      setError('You do not have permission to access Quotation Generator');
      setLoading(false);
      return;
    }

    if (!companyId) {
      setError('No company context available');
      setLoading(false);
      return;
    }

    // Load company bikes
    loadCompanyBikes();
  }, [companyId, hasPermission]);

  // Load company-specific bikes
  const loadCompanyBikes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`QuotationGenerator: Loading bikes for company ${companyId}`);
      
      const q = query(managedBikesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const loadedBikes = [];
      
      querySnapshot.forEach((doc) => {
        loadedBikes.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`QuotationGenerator: Loaded ${loadedBikes.length} bikes for company ${companyId}`);
      setBikes(loadedBikes);
      
    } catch (error) {
      console.error('QuotationGenerator: Error loading bikes:', error);
      setError('Failed to load bikes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Real-time listener for bikes
  useEffect(() => {
    if (!companyId) {
      console.warn('No company ID for bikes listener');
      return;
    }

    console.log(`QuotationGenerator: Setting up bikes listener for company: ${companyId}`);
    
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const updatedBikes = [];
      querySnapshot.forEach((doc) => {
        updatedBikes.push({ id: doc.id, ...doc.data() });
      });
      setBikes(updatedBikes);
      console.log(`QuotationGenerator: Real-time update: ${updatedBikes.length} bikes for company ${companyId}`);
    }, (error) => {
      console.error('QuotationGenerator: Error in bikes subscription:', error);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Save quote to Firebase
  const saveQuote = async () => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    try {
      const subtotal = selectedBikes.reduce((sum, bike) => sum + (bike.customRate * bike.rentalDays), 0);
      const taxAmount = subtotal * (quoteData.taxRate / 100);
      const discountAmount = subtotal * (quoteData.discountRate / 100);
      const total = subtotal + taxAmount - discountAmount;

      const quoteDataToSave = {
        ...quoteData,
        bikes: selectedBikes,
        subtotal: subtotal,
        taxAmount: taxAmount,
        discountAmount: discountAmount,
        total: total,
        companyId: companyId,
        companyName: currentCompany?.name || 'Unknown Company',
        createdAt: new Date(),
        createdBy: currentUser?.id || 'unknown',
        createdByUsername: currentUser?.username || 'Unknown User'
      };

      await addDoc(quotesCollection, quoteDataToSave);
      alert('✅ Quote saved successfully!');
      
      // Reset form
      setStep(1);
      setSelectedBikes([]);
      setQuoteData({
        quoteNumber: 'Q' + Date.now(),
        quoteDate: new Date().toISOString().split('T')[0],
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        startDate: new Date().toISOString().split('T')[0], // Today
        endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow (2 days total: today + tomorrow)
        pickupLocation: '',
        taxRate: 0,
        discountRate: 0,
        notes: ''
      });
      
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('❌ Error saving quote: ' + error.message);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (step === 1 && selectedBikes.length === 0) {
      alert('Please select at least one bike');
      return;
    }
    if (step === 2 && !quoteData.customerName.trim()) {
      alert('Please enter customer name');
      return;
    }
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleEdit = () => {
    setStep(2);
  };

  // Permission check render
  if (!hasPermission('quotationGenerator')) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to access Quotation Generator.</p>
          <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-20 w-20 text-red-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Error</h3>
          <p className="text-gray-500 text-lg">{error}</p>
          <button 
            onClick={loadCompanyBikes}
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading bike management data...</p>
          {currentCompany && (
            <p className="text-gray-500 text-sm mt-2">Company: {currentCompany.name}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h3 className="text-xl font-semibold text-white">Quotation Generator</h3>
          {currentCompany && (
            <div className="ml-4 flex items-center gap-2 text-blue-400 text-sm">
              <Building size={16} />
              <span>{currentCompany.name}</span>
              <span className="text-gray-500">•</span>
              <Bike size={16} />
              <span>{bikes.length} bikes</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-400">
            Quote #{quoteData.quoteNumber}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${step >= 1 ? 'text-orange-400' : 'text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-orange-500 text-white' : 'bg-gray-600'}`}>
              1
            </div>
            <span className="font-medium">Select Bikes</span>
          </div>
          <div className={`w-16 h-0.5 ${step > 1 ? 'bg-orange-500' : 'bg-gray-600'}`}></div>
          <div className={`flex items-center space-x-3 ${step >= 2 ? 'text-orange-400' : 'text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-orange-500 text-white' : 'bg-gray-600'}`}>
              2
            </div>
            <span className="font-medium">Quote Details</span>
          </div>
          <div className={`w-16 h-0.5 ${step > 2 ? 'bg-orange-500' : 'bg-gray-600'}`}></div>
          <div className={`flex items-center space-x-3 ${step >= 3 ? 'text-orange-400' : 'text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-orange-500 text-white' : 'bg-gray-600'}`}>
              3
            </div>
            <span className="font-medium">Preview & Save</span>
          </div>
        </div>
      </div>

      {/* Integration Notice */}
      <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Check className="text-blue-400 mt-0.5" size={20} />
          <div>
            <h4 className="text-blue-300 font-medium mb-1">Bike Management Integration Active</h4>
            <p className="text-blue-200 text-sm">
              Showing {bikes.length} bikes from your bike management system with real-time pricing and availability.
            </p>
          </div>
        </div>
      </div>

      {/* Step Content */}
      {step === 1 && (
        <BikeSelection
          bikes={bikes}
          selectedBikes={selectedBikes}
          setSelectedBikes={setSelectedBikes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          quoteData={quoteData}
        />
      )}

      {step === 2 && (
        <QuoteForm
          quoteData={quoteData}
          setQuoteData={setQuoteData}
          selectedBikes={selectedBikes}
          setSelectedBikes={setSelectedBikes}
          currentCompany={currentCompany}
          currentUser={currentUser}
        />
      )}

      {step === 3 && (
        <QuotePreview
          quoteData={quoteData}
          selectedBikes={selectedBikes}
          currentCompany={currentCompany}
          currentUser={currentUser}
          onEdit={handleEdit}
          onSave={saveQuote}
        />
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={step === 1}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <ChevronUp size={16} className="rotate-[-90deg]" />
          <span>Previous</span>
        </button>

        {step < 3 && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors flex items-center space-x-2"
          >
            <span>Next</span>
            <ChevronDown size={16} className="rotate-[-90deg]" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuotationGenerator;